import React, { useCallback, useRef, useState, useEffect } from "react";

export type UploadPhase = "idle" | "uploading" | "validating" | "analyzing" | "done";

const PHASE_INFO: Record<UploadPhase, { icon: string; label: string; detail: string }> = {
  idle: { icon: "cloud_upload", label: "", detail: "" },
  uploading: { icon: "cloud_upload", label: "Caricamento PDF…", detail: "Invio del file al server" },
  validating: { icon: "fact_check", label: "Validazione campi…", detail: "Verifica dei campi AcroForm nel PDF" },
  analyzing: { icon: "psychology", label: "Analisi AI in corso…", detail: "L'intelligenza artificiale sta analizzando i campi del documento" },
  done: { icon: "check_circle", label: "Completato!", detail: "" },
};

interface Props {
  onFile: (file: File) => void;
  isLoading?: boolean;
  uploadPhase?: UploadPhase;
}

const MAX_SIZE = 10 * 1024 * 1024; // 10 MB

export function PdfUploader({ onFile, isLoading, uploadPhase = "idle" }: Props) {
  const [dragActive, setDragActive] = useState(false);
  const [error, setError] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  const validate = useCallback((file: File): boolean => {
    setError("");
    if (!file.name.toLowerCase().endsWith(".pdf")) {
      setError("Solo file PDF sono accettati.");
      return false;
    }
    if (file.size > MAX_SIZE) {
      setError(`File troppo grande (max ${MAX_SIZE / 1024 / 1024} MB).`);
      return false;
    }
    return true;
  }, []);

  const handleFile = useCallback(
    (file: File) => {
      if (validate(file)) onFile(file);
    },
    [validate, onFile],
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setDragActive(false);
      const file = e.dataTransfer.files[0];
      if (file) handleFile(file);
    },
    [handleFile],
  );

  return (
    <div>
      <div
        className={`upload-area${dragActive ? " upload-area-active" : ""}`}
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => {
          e.preventDefault();
          setDragActive(true);
        }}
        onDragLeave={() => setDragActive(false)}
        onDrop={handleDrop}
      >
        {isLoading ? (
          <div style={{ textAlign: "center" }}>
            <span className="spinner spinner-lg" />
            <p style={{ margin: "var(--space-3) 0 var(--space-1)", fontWeight: 600 }}>
              {PHASE_INFO[uploadPhase]?.label || "Caricamento in corso…"}
            </p>
            {PHASE_INFO[uploadPhase]?.detail && (
              <p style={{ fontSize: "var(--font-size-sm)", color: "var(--indaco-gray-500)", margin: 0 }}>
                {PHASE_INFO[uploadPhase].detail}
              </p>
            )}
            <div style={{ display: "flex", justifyContent: "center", gap: 8, marginTop: "var(--space-3)" }}>
              {(["uploading", "validating", "analyzing"] as UploadPhase[]).map((phase) => {
                const phases: UploadPhase[] = ["uploading", "validating", "analyzing"];
                const currentIdx = phases.indexOf(uploadPhase);
                const phaseIdx = phases.indexOf(phase);
                const isDone = phaseIdx < currentIdx;
                const isCurrent = phase === uploadPhase;
                return (
                  <div
                    key={phase}
                    style={{
                      display: "flex", alignItems: "center", gap: 4,
                      opacity: isDone || isCurrent ? 1 : 0.35,
                      fontSize: "var(--font-size-xs)",
                      color: isDone ? "var(--indaco-success)" : isCurrent ? "var(--indaco-brand-primary)" : "var(--indaco-gray-400)",
                      fontWeight: isCurrent ? 600 : 400,
                    }}
                  >
                    <span className="material-symbols-outlined" style={{ fontSize: 16 }}>
                      {isDone ? "check_circle" : PHASE_INFO[phase].icon}
                    </span>
                    {PHASE_INFO[phase].label.replace("…", "")}
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          <>
            <span
              className="material-symbols-outlined"
              style={{ fontSize: 48, color: "var(--indaco-brand-primary)" }}
            >
              cloud_upload
            </span>
            <p style={{ margin: "var(--space-3) 0 var(--space-1)", fontWeight: 600 }}>
              Trascina qui il tuo PDF
            </p>
            <p style={{ fontSize: "var(--font-size-sm)", color: "var(--indaco-gray-600)" }}>
              oppure clicca per selezionare (max 10 MB)
            </p>
          </>
        )}
      </div>

      <input
        ref={inputRef}
        type="file"
        accept=".pdf"
        style={{ display: "none" }}
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) handleFile(file);
          e.target.value = "";
        }}
      />

      {error && (
        <div
          style={{
            marginTop: "var(--space-2)",
            color: "var(--indaco-danger)",
            fontSize: "var(--font-size-sm)",
            display: "flex",
            alignItems: "center",
            gap: "var(--space-1)",
          }}
        >
          <span className="material-symbols-outlined" style={{ fontSize: 16 }}>
            error
          </span>
          {error}
        </div>
      )}
    </div>
  );
}
